import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useToast } from '../../hooks/useToast';

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  submittedAt: Date;
  type: 'contact';
}

interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  issueType: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  type: 'support';
}

type Message = ContactSubmission | SupportTicket;

const MessagesPage: React.FC = () => {
  const [contactSubmissions, setContactSubmissions] = useState<
    ContactSubmission[]
  >([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'contact' | 'support' | 'all'>(
    'all'
  );
  const [filter, setFilter] = useState<string>('all');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    loadAllMessages();
  }, []);

  const loadAllMessages = async () => {
    try {
      setLoading(true);

      // Load Contact Submissions
      const contactRef = collection(db, 'contactSubmissions');
      const contactQuery = query(contactRef, orderBy('submittedAt', 'desc'));
      const contactSnapshot = await getDocs(contactQuery);

      const contactData: ContactSubmission[] = [];
      contactSnapshot.forEach((doc) => {
        const data = doc.data();
        contactData.push({
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate() || new Date(),
          type: 'contact',
        } as ContactSubmission);
      });

      setContactSubmissions(contactData);

      // Load Support Tickets
      const supportRef = collection(db, 'supportTickets');
      const supportQuery = query(supportRef, orderBy('createdAt', 'desc'));
      const supportSnapshot = await getDocs(supportQuery);

      const supportData: SupportTicket[] = [];
      supportSnapshot.forEach((doc) => {
        const data = doc.data();
        supportData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          type: 'support',
        } as SupportTicket);
      });

      setSupportTickets(supportData);
    } catch (error) {
      console.error('Error loading messages:', error);
      showToast({ type: 'error', message: 'Failed to load messages' });
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (
    submissionId: string,
    newStatus: ContactSubmission['status']
  ) => {
    try {
      const submissionRef = doc(db, 'contactSubmissions', submissionId);
      await updateDoc(submissionRef, { status: newStatus });

      setContactSubmissions(
        contactSubmissions.map((sub) =>
          sub.id === submissionId ? { ...sub, status: newStatus } : sub
        )
      );

      if (selectedMessage?.id === submissionId) {
        setSelectedMessage({
          ...selectedMessage,
          status: newStatus,
        } as Message);
      }

      showToast({ type: 'success', message: 'Status updated successfully' });
    } catch (error) {
      console.error('Error updating status:', error);
      showToast({ type: 'error', message: 'Failed to update status' });
    }
  };

  const updateSupportStatus = async (
    ticketId: string,
    newStatus: SupportTicket['status']
  ) => {
    try {
      const ticketRef = doc(db, 'supportTickets', ticketId);
      await updateDoc(ticketRef, { status: newStatus });

      setSupportTickets(
        supportTickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
        )
      );

      if (selectedMessage?.id === ticketId) {
        setSelectedMessage({
          ...selectedMessage,
          status: newStatus,
        } as Message);
      }

      showToast({ type: 'success', message: 'Status updated successfully' });
    } catch (error) {
      console.error('Error updating status:', error);
      showToast({ type: 'error', message: 'Failed to update status' });
    }
  };

  const deleteMessage = async (
    messageId: string,
    messageType: 'contact' | 'support'
  ) => {
    if (!confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      const collectionName =
        messageType === 'contact' ? 'contactSubmissions' : 'supportTickets';
      const messageRef = doc(db, collectionName, messageId);
      await deleteDoc(messageRef);

      if (messageType === 'contact') {
        setContactSubmissions(
          contactSubmissions.filter((sub) => sub.id !== messageId)
        );
      } else {
        setSupportTickets(
          supportTickets.filter((ticket) => ticket.id !== messageId)
        );
      }

      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }

      showToast({
        type: 'success',
        message: 'Message deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting message:', error);
      showToast({ type: 'error', message: 'Failed to delete message' });
    }
  };

  const getContactStatusBadgeColor = (status: ContactSubmission['status']) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-yellow-100 text-yellow-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSupportStatusBadgeColor = (status: SupportTicket['status']) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Combine and filter messages based on active tab and filter
  const getAllMessages = (): Message[] => {
    let messages: Message[] = [];

    if (activeTab === 'contact' || activeTab === 'all') {
      messages = [...messages, ...contactSubmissions];
    }

    if (activeTab === 'support' || activeTab === 'all') {
      messages = [...messages, ...supportTickets];
    }

    // Sort by date
    messages.sort((a, b) => {
      const dateA = 'submittedAt' in a ? a.submittedAt : a.createdAt;
      const dateB = 'submittedAt' in b ? b.submittedAt : b.createdAt;
      return dateB.getTime() - dateA.getTime();
    });

    // Apply status filter
    if (filter !== 'all') {
      messages = messages.filter((msg) => msg.status === filter);
    }

    return messages;
  };

  const filteredMessages = getAllMessages();

  // Calculate counts for filters (from all messages in current tab, before status filter)
  const getFilterCounts = () => {
    let messages: Message[] = [];

    if (activeTab === 'contact' || activeTab === 'all') {
      messages = [...messages, ...contactSubmissions];
    }

    if (activeTab === 'support' || activeTab === 'all') {
      messages = [...messages, ...supportTickets];
    }

    const counts: Record<string, number> = {
      all: messages.length,
    };

    messages.forEach((msg) => {
      counts[msg.status] = (counts[msg.status] || 0) + 1;
    });

    return counts;
  };

  const filterCounts = getFilterCounts();

  // Get available filter options based on active tab
  const getFilterOptions = (): string[] => {
    if (activeTab === 'contact') {
      return ['all', 'new', 'read', 'replied', 'archived'];
    } else if (activeTab === 'support') {
      return ['all', 'open', 'in-progress', 'resolved', 'closed'];
    } else {
      // For 'all' tab, show all possible statuses
      return [
        'all',
        'new',
        'read',
        'replied',
        'archived',
        'open',
        'in-progress',
        'resolved',
        'closed',
      ];
    }
  };

  const filterOptions = getFilterOptions();

  // Reset filter when changing tabs
  useEffect(() => {
    setFilter('all');
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
        <p className="text-gray-600 mt-1">
          View and manage contact submissions and support tickets
        </p>
      </div>

      {/* Main Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Messages ({contactSubmissions.length + supportTickets.length})
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'contact'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Contact Submissions ({contactSubmissions.length})
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'support'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Support Tickets ({supportTickets.length})
            </button>
          </nav>
        </div>

        {/* Status Filter Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex -mb-px overflow-x-auto">
            {filterOptions.map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`py-3 px-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                  filter === status
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {status.charAt(0).toUpperCase() +
                  status.slice(1).replace('-', ' ')}{' '}
                ({filterCounts[status] || 0})
              </button>
            ))}
          </nav>
        </div>

        {/* Messages Table */}
        <div className="overflow-x-auto">
          {filteredMessages.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No messages
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                No messages found with the selected filters.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMessages.map((message) => (
                  <tr
                    key={message.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedMessage(message)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          message.type === 'contact'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {message.type === 'contact' ? 'Contact' : 'Support'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {message.type === 'contact'
                        ? message.name
                        : message.userEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {message.type === 'support' ? message.issueType : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate">{message.message}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          message.type === 'contact'
                            ? getContactStatusBadgeColor(
                                message.status as ContactSubmission['status']
                              )
                            : getSupportStatusBadgeColor(
                                message.status as SupportTicket['status']
                              )
                        }`}
                      >
                        {message.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(message.type === 'contact'
                        ? message.submittedAt
                        : message.createdAt
                      ).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMessage(message.id, message.type);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedMessage.type === 'contact'
                      ? 'Contact Submission'
                      : 'Support Ticket'}
                  </h2>
                  <span
                    className={`inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full ${
                      selectedMessage.type === 'contact'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {selectedMessage.type === 'contact'
                      ? 'Contact Form'
                      : 'Support Ticket'}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {selectedMessage.type === 'contact' ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.name}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.email}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Message
                      </label>
                      <p className="mt-1 text-gray-900 whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Submitted
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.submittedAt.toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        Status
                      </label>
                      <select
                        value={selectedMessage.status}
                        onChange={(e) =>
                          updateContactStatus(
                            selectedMessage.id,
                            e.target.value as ContactSubmission['status']
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        User Email
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.userEmail}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Issue Type
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.issueType}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Message
                      </label>
                      <p className="mt-1 text-gray-900 whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Created
                      </label>
                      <p className="mt-1 text-gray-900">
                        {selectedMessage.createdAt.toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        Status
                      </label>
                      <select
                        value={selectedMessage.status}
                        onChange={(e) =>
                          updateSupportStatus(
                            selectedMessage.id,
                            e.target.value as SupportTicket['status']
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() =>
                    deleteMessage(selectedMessage.id, selectedMessage.type)
                  }
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                >
                  Delete
                </button>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
